import React from 'react';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  fromPromise,
  NormalizedCacheObject,
  Observable,
} from '@apollo/client';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { onError } from '@apollo/client/link/error';

import { ZVUK_API_GRAPHQL_URL } from 'application/consts';

import { setTokenCookie, removeTokenCookie } from '@shared/lib';
import { setAuthToken } from 'application/store/slices/applicationSlice/slice';

import {
  UserBillingInfo,
  PaymentMethodStatusType,
  OrganizationPricePlanPureType,
} from '@shared/api/graphql/typePolicies/billing';

import DataProviderUtils from './utils';

const httpLink = createUploadLink({ uri: ZVUK_API_GRAPHQL_URL });
const inMemoryCache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        adcampaignsPaginationQuery: {
          keyArgs: ['filters'],
        },
        pointsPaginationQuery: {
          keyArgs: ['filters'],
        },
        streamMediafiles: {
          keyArgs: ['filters'],
        },
        streamMediafilesEvents: {
          keyArgs: ['filters'],
        },
      },
    },
    RequisiteRUPureType: {
      keyFields: ['emailForDocuments'],
    },
    UserBillingInfo,
    PaymentMethodStatusType,
    OrganizationPricePlanPureType,
    UserExtendPureType: {
      fields: {
        companies: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

export const apolloClient = new ApolloClient<NormalizedCacheObject>({
  link: httpLink,
  cache: inMemoryCache,
});

let appDispatch: ((action: ReturnType<typeof setAuthToken>) => void) | null = null;
export const setAppDispatch = (dispatch: typeof appDispatch) => {
  appDispatch = dispatch;
};

let pendingRequests: Function[] = [];
let isRefreshing = false;
let isLoggingOut = false;

const resolvePendingRequests = () => {
  pendingRequests.map((callback) => callback());
  pendingRequests = [];
};

const errorLink = onError(
  ({
    graphQLErrors,
    operation,
    forward,
    // eslint-disable-next-line consistent-return
  }) => {
    if (graphQLErrors) {
      const isNeedRequestJWT = DataProviderUtils.checkIsJWTExpiredError(graphQLErrors);

      if (isNeedRequestJWT) {
        if (!isRefreshing) {
          isRefreshing = true;

          return fromPromise(
            DataProviderUtils.tokenRefresh()
              .then((authToken) => {
                if (authToken) {
                  setTokenCookie(authToken);
                } else {
                  isLoggingOut = true;
                  removeTokenCookie();
                  appDispatch?.(setAuthToken(undefined));
                }
              })
              .catch(() => {
                isLoggingOut = true;
                removeTokenCookie();
                appDispatch?.(setAuthToken(undefined));
                resolvePendingRequests();
                isRefreshing = false;
              })
          ).flatMap(() => {
            resolvePendingRequests();
            isRefreshing = false;
            return forward(operation);
          });
        }

        return fromPromise(
          new Promise<void>((resolve) => {
            pendingRequests.push(() => resolve());
          })
        ).flatMap(() => forward(operation));
      }

      const isAuthError = DataProviderUtils.checkIsAuthError(graphQLErrors);

      if (isAuthError) {
        if (!isLoggingOut) {
          isLoggingOut = true;
          removeTokenCookie();
          appDispatch?.(setAuthToken(undefined));
        }

        return new Observable((subscriber) => {
          subscriber.complete();
        });
      }
    }
  }
);

apolloClient.setLink(errorLink.concat(httpLink));

const DataProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
);

export default DataProvider;
